import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import VoiceCommandPanel from "./VoiceCommandPanel";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import { useTheme } from "../../contexts/ThemeContext";
import { formatFriendlyTimestamp } from '../../utils/helpers';
import { logout, getItem } from "../../utils/authStorage";
import { parseJwt } from "../../utils/jwtDecoder";
import { TablerHigh, TablerMedium, TablerLow } from "../shared/Icons";
import "./VoicePrompt.css";
import "./AlertsPage.css";
import CustomSelect from "../shared/CustomSelect";
import { ROWS_PER_PAGE_OPTIONS } from "../shared/selectOptions";

const VoicePrompts = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [voiceResponse, setVoiceResponse] = useState(() => {
    const saved = sessionStorage.getItem("voice_response");
    return saved ? JSON.parse(saved) : null;
  });
  const [alerts, setAlerts] = useState(() => {
    const saved = sessionStorage.getItem("voice_alerts");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem("voice_page");
    return saved ? Number(saved) : 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = sessionStorage.getItem("voice_rows_per_page");
    return saved ? Number(saved) : 10;
  });

  React.useEffect(() => {
    if (voiceResponse) {
      sessionStorage.setItem("voice_response", JSON.stringify(voiceResponse));
    } else {
      sessionStorage.removeItem("voice_response");
    }
  }, [voiceResponse]);

  React.useEffect(() => {
    if (alerts && alerts.length > 0) {
      sessionStorage.setItem("voice_alerts", JSON.stringify(alerts));
    } else {
      sessionStorage.removeItem("voice_alerts");
    }
  }, [alerts]);

  React.useEffect(() => {
    sessionStorage.setItem("voice_page", String(currentPage));
  }, [currentPage]);

  React.useEffect(() => {
    sessionStorage.setItem("voice_rows_per_page", String(rowsPerPage));
  }, [rowsPerPage]);
  
  const [sortField, setSortField] = useState('start_time');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const token = getItem("accessToken");
  const tokenPayload = token ? parseJwt(token) : null;
  const username =
    getItem("username") ||
    tokenPayload?.username ||
    tokenPayload?.preferred_username ||
    "User";
  const rawRole =
    getItem("role") || tokenPayload?.role || tokenPayload?.roles?.[0] || "Operator";
  const role = rawRole.toLowerCase();

  const handleSignOut = () => {
    logout();
  };

  const hasRoleFn = (allowedRoles) => {
    const userRole = (getItem("role") || "operator").toLowerCase();
    return allowedRoles.includes(userRole);
  };

  const handleVoiceResponse = (response) => {
    setVoiceResponse(response.command);
    setAlerts(response.alerts || []);
    setCurrentPage(1);
  };

  const handleBackToMic = () => {
    setVoiceResponse(null);
    setAlerts([]);
    setCurrentPage(1);
    sessionStorage.removeItem("voice_response");
    sessionStorage.removeItem("voice_alerts");
    sessionStorage.removeItem("voice_page");
  };

  const sortedAlerts = [...alerts].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (sortField === 'start_time') {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }

    if (sortField === 'severity') {
      const severityRank = { low: 1, medium: 2, high: 3 };
      valA = severityRank[a.severity?.toLowerCase()] || 0;
      valB = severityRank[b.severity?.toLowerCase()] || 0;
    }

    return sortOrder === 'asc' ? valA - valB : valB - valA;
  });

  const paginatedAlerts = sortedAlerts.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Helpers for grid formatting matching AlertsPage
  const truncateId = (id) => {
    if (!id) return "Unknown ID";
    if (id.length <= 16) return id;
    return `${id.substring(0, 8)}…${id.substring(id.length - 6)}`;
  };

  const getSeverityClass = (severity) => {
    if (!severity) return "badge-severity-low";
    const s = severity.toLowerCase();
    if (s === "high") return "badge-severity-high";
    if (s === "medium") return "badge-severity-medium";
    return "badge-severity-low";
  };

  const getSeverityIcon = (severity) => {
    if (!severity) return null;
    const s = severity.toLowerCase();
    if (s === "high") return <TablerHigh size={14} />;
    if (s === "medium") return <TablerMedium size={14} />;
    if (s === "low") return <TablerLow size={14} />;
    return null;
  };

  const formatBadgeLabel = (value) => {
    if (!value) return "N/A";
    return String(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusClass = (status) => {
    if (!status) return "badge-status-default";
    const s = status.toLowerCase();
    if (s === "active") return "badge-status-active";
    if (s === "processed") return "badge-status-processed";
    if (s === "completed" || s === "complete") return "badge-status-completed";
    return "badge-status-default";
  };

  const totalPages = Math.max(1, Math.ceil(alerts.length / rowsPerPage));

  return (
    <div className={`voice-page-wrapper alerts-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />
      
      <div className="voice-main-content">
        {/* Top Header Bar */}
        <Header title={t("nav.voiceAlerts")} />

        {/* Horizontal Divider */}
        <div className="voice-main-divider" />

        {/* Voice Page Body Container */}
        <div className="voice-body-container">
          {!voiceResponse ? (
            <div className="voice-panel-center">
              <VoiceCommandPanel onResponse={handleVoiceResponse} showTitle={false} />
            </div>
          ) : (
            <div className="voice-results-container">
              {/* Back to Mic Button */}
              <button className="voice-back-btn" onClick={handleBackToMic}>
                {t("button.speakAnotherCommand")}
              </button>

              <div className="voice-meta-row">
                <div className="voice-status-box">
                  <strong>{t("label.statusLabel")}:</strong> {t("label.statusHandsFree")}
                </div>

                <div className="voice-heard-box">
                  <strong>{t("label.commandHeard")}</strong> "{voiceResponse.replace(/hello applied/i, '').trim()}"
                </div>
              </div>

              <h3 className="voice-table-title">{t("label.voiceResults", { count: alerts.length })}</h3>
              
              <div className="alerts-table-container">
                <div className="alerts-grid-table">
                  <div className="alerts-grid-header">
                    <div className="alerts-grid-cell alerts-grid-cell-first">{t("label.alertId")}</div>
                    <div 
                      className="alerts-grid-cell sortable-th" 
                      onClick={() => toggleSort('start_time')}
                      style={{ cursor: 'pointer' }}
                    >
                      {t("label.timestampCol")} {sortField === 'start_time' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </div>
                    <div 
                      className="alerts-grid-cell sortable-th" 
                      onClick={() => toggleSort('severity')}
                      style={{ cursor: 'pointer' }}
                    >
                      {t("label.severity")} {sortField === 'severity' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </div>
                    <div className="alerts-grid-cell">{t("label.message")}</div>
                    <div className="alerts-grid-cell">{t("label.statusLabel")}</div>
                    <div className="alerts-grid-cell alerts-grid-cell-last">{t("label.action")}</div>
                  </div>

                  <div className="alerts-grid-body">
                    {paginatedAlerts.length === 0 ? (
                      <div className="alerts-grid-row" style={{ display: 'flex', justifyContent: "center", padding: "30px", color: "var(--text-muted)", fontStyle: "italic" }}>
                        {t("label.noAlertsCommand")}
                      </div>
                    ) : (
                      paginatedAlerts.map((alert, index) => (
                        <div className="alerts-grid-row" key={alert.id || index}>
                          <div className="alerts-grid-cell alerts-grid-cell-first">
                            <Link
                              className="alerts-link-button"
                              to={`/alert-details?alertId=${encodeURIComponent(
                                alert.id
                              )}&type=${alert.type || "camera"}`}
                            >
                              {truncateId(alert.id)}
                            </Link>
                          </div>
                          <div className="alerts-grid-cell">
                            {formatFriendlyTimestamp(alert.start_time)}
                          </div>
                          <div className="alerts-grid-cell">
                            <span className={`alerts-badge ${getSeverityClass(alert.severity)}`}>
                              {getSeverityIcon(alert.severity)}
                              {formatBadgeLabel(alert.severity)}
                            </span>
                          </div>
                          <div className="alerts-grid-cell alerts-description-cell" style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="alerts-description-text" style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%'
                            }}>
                              {alert.message || "No description provided."}
                            </span>
                          </div>
                          <div className="alerts-grid-cell">
                            <span className={`alerts-badge ${getStatusClass(alert.alert_analysis)}`}>
                              {formatBadgeLabel(alert.alert_analysis)}
                            </span>
                          </div>
                          <div className="alerts-grid-cell alerts-grid-cell-last">
                            <Link
                              className="alerts-view-devices-btn"
                              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              to={`/alert-details?alertId=${encodeURIComponent(
                                alert.id
                              )}&type=${alert.type || "camera"}`}
                            >
                              {t("button.viewDetails")}
                            </Link>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Pagination controls */}
              {alerts.length > 0 && (
                <div className="alerts-pagination">
                  <div className="alerts-pagination-left">
                    <span className="alerts-pagination-label">{t("label.rows")}</span>
                    <CustomSelect
                      value={rowsPerPage}
                      onChange={(val) => {
                        setRowsPerPage(Number(val));
                        setCurrentPage(1);
                      }}
                      options={ROWS_PER_PAGE_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                      className="alerts-rows-custom-select"
                    />
                    <span className="alerts-pagination-info">
                      {`${Math.min(
                        (currentPage - 1) * rowsPerPage + 1,
                        alerts.length
                      )} - ${Math.min(
                        currentPage * rowsPerPage,
                        alerts.length
                      )} of ${alerts.length}`}
                    </span>
                  </div>
                  <div className="alerts-pagination-right">
                    <button
                      className="alerts-page-arrow"
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                        <path
                          d="M7.5 1L1.5 7L7.5 13"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      className="alerts-page-arrow"
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                        <path
                          d="M1.5 1L7.5 7L1.5 13"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoicePrompts;
